/**
 * URL とステージの対応づけ（純粋ロジック / DOM 非依存）。
 *
 * クエリ（`?stage=...`）ではなく**パス**（`/hokkaido`）にしている。
 * Cloudflare Web Analytics はクエリ文字列を記録しないため、
 * クエリにすると「どの県が遊ばれているか」が解析に出ない。
 * 47県やる以上ここは測れないと困るので、パスを採用している。
 */

/** 地域IDだけが指定されたときに補う既定のモード。 */
export const DEFAULT_STAGE_MODE = 'food';

/** パスの先頭セグメントを取り出す（空なら null）。 */
function firstSegment(pathname: string): string | null {
  const segments = pathname.split('/').filter((part) => part.length > 0);
  if (segments.length === 0) return null;
  try {
    return decodeURIComponent(segments[0]).toLowerCase();
  } catch {
    return segments[0].toLowerCase();
  }
}

/**
 * トップ（県を選ぶ画面）かどうか。
 *
 * `/` と `/index.html` だけがトップ。それ以外は県のページとして扱う。
 * 47県あるので、トップでいきなり1県が立ち上がると「その県のゲーム」に見えてしまう。
 */
export function isRootPath(pathname: string): boolean {
  const segment = firstSegment(pathname);
  return segment === null || segment === 'index.html';
}

/**
 * URL のパスからステージIDを決める。
 *
 * - `/`                 → 既定ステージ
 * - `/fukuoka`          → `fukuoka-food`（地域IDに既定モードを補う）
 * - `/fukuoka-food`     → そのまま
 * - 未知のパス          → 既定ステージ（404 にはしない）
 */
export function resolveStageIdFromPath(
  pathname: string,
  availableIds: readonly string[],
  defaultStageId: string,
): string {
  const segment = firstSegment(pathname);
  if (!segment) return defaultStageId;
  if (availableIds.includes(segment)) return segment;

  const withMode = `${segment}-${DEFAULT_STAGE_MODE}`;
  if (availableIds.includes(withMode)) return withMode;

  return defaultStageId;
}

/**
 * ステージIDから、共有しやすい短いパスを作る。
 *
 * 既定モードのステージは地域IDだけにする（`fukuoka-food` → `/fukuoka`）。
 * SNSや記事に貼られるURLなので、短く読める形を優先する。
 */
export function stagePath(stageId: string): string {
  const suffix = `-${DEFAULT_STAGE_MODE}`;
  const trimmed = stageId.endsWith(suffix) ? stageId.slice(0, -suffix.length) : stageId;
  return `/${trimmed}`;
}
